import { EMPTY_INVESTMENT_OPTIMIZATION_COMPARISON } from "../models/investmentOptimizationComparison";

const number = (...values) => {
  const value = values.find((candidate) => candidate !== null && candidate !== undefined &&
    candidate !== "" && Number.isFinite(Number(candidate)));
  return value === undefined ? null : Number(value);
};

const allocationByScenario = (rows) => new Map(
  (Array.isArray(rows) ? rows : []).map((row) => [String(row.scenarioId), row])
);

const isSelected = (row) => number(row?.investment) > 0;

/** Project authoritative Investment Optimization output without selecting a portfolio. */
export function investmentOptimizationAdapter(source) {
  const unconstrained = source?.unconstrainedEconomicOptimization;
  const constrained = source?.constrainedEconomicOptimization;
  const optimum = unconstrained?.optimalPortfolio;
  const executable = constrained?.optimalPortfolio;
  if (!unconstrained || !constrained) return EMPTY_INVESTMENT_OPTIMIZATION_COMPARISON;

  const optimalRows = allocationByScenario(unconstrained.scenarioAllocation);
  const constrainedRows = allocationByScenario(constrained.scenarioAllocation);
  const scenarioIds = [...new Set([...optimalRows.keys(), ...constrainedRows.keys()])];

  return {
    contractVersion: "1.0",
    status: unconstrained.status === "OPTIMAL" && constrained.status === "OPTIMAL" ? "AVAILABLE" : "UNAVAILABLE",
    currency: constrained.currency || unconstrained.currency || source?.currency || "EUR",
    currentPosition: {
      expectedAnnualLoss: number(source?.metrics?.eal?.baseline, source?.summary?.expected_annual_loss),
      p95: number(source?.metrics?.p95?.baseline, source?.summary?.p95),
      capitalAtRisk: number(source?.metrics?.capital_at_risk?.baseline, source?.summary?.capital_at_risk),
      exposureSummary: source?.exposureSummary || source?.summary?.exposure_summary || null,
      scenarioCount: number(source?.summary?.scenario_count, source?.configuration?.scenarioIds?.length),
    },
    economicOpportunity: {
      investment: number(optimum?.investment),
      expectedRiskReduction: number(optimum?.expectedRiskReduction),
      capitalEfficiency: number(optimum?.capitalEfficiency),
      selectedScenarios: [...optimalRows.values()].filter(isSelected),
    },
    executablePosition: {
      availableCapital: number(constrained.availableCapital),
      selectedInvestment: number(constrained.selectedInvestment, executable?.investment),
      capturedRiskReduction: number(executable?.expectedRiskReduction),
      unallocatedCapital: number(constrained.unallocatedCapital),
      capitalConstraintBinding: Boolean(constrained.capitalConstraintBinding),
      selectedScenarios: [...constrainedRows.values()].filter(isSelected),
    },
    opportunityGap: {
      additionalRiskReductionOpportunity: number(constrained.valueLeftOnTable?.riskReductionDifference),
      additionalCapitalRequired: number(constrained.valueLeftOnTable?.investmentDifference),
      economicValueLeftOnTable: number(constrained.valueLeftOnTable?.economicValueDifference),
      opportunityCapturedPercentage: number(constrained.valueLeftOnTable?.percentageOfOpportunityCaptured),
      riskReductionCapturedPercentage: number(constrained.valueLeftOnTable?.percentageOfRiskReductionCaptured),
    },
    scenarioAttribution: scenarioIds.map((scenarioId) => {
      const optimal = optimalRows.get(scenarioId);
      const selected = constrainedRows.get(scenarioId);
      return {
        scenarioId,
        scenarioName: optimal?.scenarioName || selected?.scenarioName || `Scenario ${scenarioId}`,
        optimalAllocation: optimal || null,
        constrainedAllocation: selected || null,
        selectionState: isSelected(optimal)
          ? (isSelected(selected) ? "FUNDED_BOTH" : "NOT_SELECTED_CONSTRAINED")
          : (isSelected(selected) ? "SELECTED_CONSTRAINED_ONLY" : "NOT_SELECTED_EITHER"),
        suppliedReason: selected?.selectionReason || selected?.reason || selected?.constraintReason || null,
        unrealizedInvestment: number(optimal?.investment) == null || number(selected?.investment) == null
          ? null : Math.max(0, number(optimal?.investment) - number(selected?.investment)),
        unrealizedRiskReduction: number(optimal?.riskReductionContribution) == null || number(selected?.riskReductionContribution) == null
          ? null : Math.max(0, number(optimal?.riskReductionContribution) - number(selected?.riskReductionContribution)),
      };
    }),
  };
}

export default investmentOptimizationAdapter;
